import { useMutation, useQuery, useQueryClient } from "react-query";
import { menuHttp } from "./menus-http";
import {
  DeleteMenuData,
  Menu,
  MenuDraftResetOrPublish,
  MenuProduct,
  MenuRecommendedProduct,
  PublishDestinations,
  PublishMenusToDestinations,
  UpdateMenuData,
  UpdateMenuDraftProducts,
  UpdateRecommendedProductsPayload,
} from "../models/Menus";
import { OrganizationKeyName } from "../../../model/Organization";

export const cacheKeys = {
  menus: "menus",
  menu: "menu",
  menuProducts: "menu-products",
  menuDraftProducts: "menu-draft-products",
  menuAvailableProducts: "menu-available-products",
  menuRestaurants: "menu-restaurants",
  menuSalesChannels: "menu-sales-channels",
  menuPublishDestinations: "menu-published-destinations",
  menuRecommendedProducts: "menu-recommended-products",
  menuOverwrites: "menu-overwrites",
  menuOverwrite: "menu-overwrite",
  menuCampaigns: "menu-campaigns",
};

export const useGetMenus = (organizationKeyName: OrganizationKeyName) => {
  return useQuery<Menu[], any>(
    [cacheKeys.menus, organizationKeyName],
    () => menuHttp.getMenus(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetMenu = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<Menu, any>(
    [cacheKeys.menu, id],
    () => menuHttp.getMenu(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useGetMenuProducts = (
  organizationKeyName: OrganizationKeyName,
  id?: string,
  enabled: boolean = true
) => {
  return useQuery<MenuProduct[], any>(
    [cacheKeys.menuProducts, id],
    () => menuHttp.getMenuProducts(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id && enabled }
  );
};

export const useGetMenuDraftProducts = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery<MenuProduct[], any>(
    [cacheKeys.menuDraftProducts, id],
    () => menuHttp.getMenuDraftProducts(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useGetMenuAvailableProducts = (
  organizationKeyName: OrganizationKeyName,
  enabled: boolean = true,
  id?: string,
  filters?: { tags?: string[] }
) => {
  return useQuery<MenuProduct[], any>(
    [cacheKeys.menuAvailableProducts, id, filters],
    () => menuHttp.getAvailableMenuProducts(organizationKeyName, id, filters),
    { refetchOnWindowFocus: false, enabled: !!id && enabled }
  );
};

export const useGetSalesChannelsUsingMenu = (
  organizationKeyName: OrganizationKeyName,
  id: string | undefined,
  enabled: boolean = true
) => {
  return useQuery(
    [cacheKeys.menuRestaurants, id, enabled],
    () => menuHttp.getMenuSalesChannels(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: enabled }
  );
};

export const useGetMenuSalesChannels = (
  organizationKeyName: OrganizationKeyName,
  menuId?: string,
  enabled: boolean = true
) => {
  return useQuery(
    [cacheKeys.menuSalesChannels, menuId],
    () => menuHttp.getSalesChannelsFromMenu(organizationKeyName, menuId),
    { refetchOnWindowFocus: false, enabled: !!menuId && enabled }
  );
};

export const useGetPublishDestinations = (
  organizationKeyName: OrganizationKeyName,
  enabled: boolean = true
) => {
  return useQuery<PublishDestinations, any>(
    [cacheKeys.menuPublishDestinations, organizationKeyName],
    () => menuHttp.getPublishDestinations(organizationKeyName),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useCreateMenu = () => {
  const queryClient = useQueryClient();
  return useMutation(menuHttp.createMenu, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.menus]);
      queryClient.refetchQueries([cacheKeys.menuPublishDestinations]);
    },
  });
};

export const useDeleteMenu = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, DeleteMenuData>(menuHttp.deleteMenu, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.menus]);
      queryClient.refetchQueries([cacheKeys.menuPublishDestinations]);
    },
  });
};

export const useUpdateMenu = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateMenuData>(menuHttp.updateMenu, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.menus]);
      queryClient.refetchQueries([cacheKeys.menuPublishDestinations]);
      if (id) {
        queryClient.refetchQueries([cacheKeys.menu, id]);
      }
    },
  });
};

export const useUpdateMenuDraftProducts = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateMenuDraftProducts>(
    menuHttp.updateMenuDraftProducts,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.menus]);
        if (id) {
          queryClient.refetchQueries([cacheKeys.menuAvailableProducts, id]);
        }
      },
    }
  );
};

export const useResetMenuDraft = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, MenuDraftResetOrPublish>(
    menuHttp.resetMenuDraft,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.menus]);
        if (id) {
          queryClient.refetchQueries([cacheKeys.menuDraftProducts, id]);
          queryClient.refetchQueries([cacheKeys.menuProducts, id]);
          queryClient.refetchQueries([cacheKeys.menuAvailableProducts, id]);
          queryClient.refetchQueries([cacheKeys.menu, id]);
        }
      },
    }
  );
};

export const usePublishMenusToDestinations = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, PublishMenusToDestinations>(
    menuHttp.publishMenusToDestinations,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.menuSalesChannels]);
        queryClient.refetchQueries([cacheKeys.menus]);
        queryClient.refetchQueries([cacheKeys.menuPublishDestinations]);
      },
    }
  );
};

export const usePublishMenuDraft = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, MenuDraftResetOrPublish>(
    menuHttp.publishMenuDraft,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.menus]);
        queryClient.refetchQueries([cacheKeys.menuPublishDestinations]);
        if (id) {
          queryClient.refetchQueries([cacheKeys.menuDraftProducts, id]);
          queryClient.refetchQueries([cacheKeys.menuProducts, id]);
          queryClient.refetchQueries([cacheKeys.menuAvailableProducts, id]);
          queryClient.refetchQueries([cacheKeys.menu, id]);
        }
      },
    }
  );
};

export const usePushMenuOnSalesChannel = () => {
  return useMutation(menuHttp.pushMenuOnSalesChannel, {
    retry: false,
  });
};

export const useBulkPushMenusForSalesChannels = () => {
  return useMutation(menuHttp.pushMenusForSalesChannels, {
    retry: false,
  });
};

export const usePushMenuToSalesChannels = (id?: string) => {
  return useMutation(menuHttp.pushMenuToSalesChannels, {
    retry: false,
  });
};

export const useGetMenuRecommendedProducts = (
  organizationKeyName?: OrganizationKeyName,
  menuId?: string
) => {
  return useQuery<MenuRecommendedProduct[], any>(
    [cacheKeys.menuRecommendedProducts, menuId],
    () => menuHttp.getMenuRecommendedProducts(organizationKeyName, menuId),
    { refetchOnWindowFocus: false }
  );
};

export const useUpdateMenuRecommendedProducts = (menuId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<any, any, UpdateRecommendedProductsPayload>(
    menuHttp.updateMenuRecommendedProducts,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.menuRecommendedProducts, menuId]);
      },
    }
  );
};

export const useGetMenuOverwrites = (
  organizationKeyName?: OrganizationKeyName,
  menuId?: string
) => {
  return useQuery(
    [cacheKeys.menuOverwrites, menuId],
    () => menuHttp.getMenuOverwrites(organizationKeyName, menuId),
    { refetchOnWindowFocus: false }
  );
};

export const useCreateMenuOverwrite = () => {
  const queryClient = useQueryClient();

  return useMutation(menuHttp.createMenuOverwrite, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.menuOverwrites]);
    },
  });
};

export const useUpdateMenuOverwrite = () => {
  const queryClient = useQueryClient();

  return useMutation(menuHttp.updateMenuOverwrite, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.menuOverwrites]);
    },
  });
};

export const useDeleteMenuOverwrite = () => {
  const queryClient = useQueryClient();
  return useMutation(menuHttp.deleteMenuOverwrite, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.menuOverwrites]);
    },
  });
};

export const useAddCampaignToMenu = () => {
  return useMutation(menuHttp.addCampaignToMenu);
};

export const useRemoveCampaignFromMenu = (menuId?: string) => {
  const queryClient = useQueryClient();
  return useMutation(menuHttp.removeCampaignFromMenu, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.menuCampaigns, menuId]);
    },
  });
};

export const useGetMenuCampaigns = (
  organizationKeyName?: OrganizationKeyName,
  menuId?: string
) => {
  return useQuery(
    [cacheKeys.menuCampaigns, menuId],
    () => menuHttp.getMenuCampaigns(organizationKeyName, menuId),
    { refetchOnWindowFocus: false }
  );
};
